import Box from '@mui/material/Box'
import { useDispatch, useSelector } from 'react-redux'
import BuildingObjectsComponent from './ArchitecturePage/ObjectTools'
import { RootState } from '../../../store'
import {
  ConstructionPage,
  ConstructionTab,
  EditorDocumentsTab,
  EditorTab,
} from '../../../reducers/construction'
import ValidationTab from '../../Validation/ValidationTab'
import { Button, Divider, IconButton, SvgIcon, Tab } from '@mui/material'
import { createSelector } from '@reduxjs/toolkit'
import { Fragment, useEffect, useRef, useState } from 'react'
import StructuralDocumentsComponent from '../../StructuralDocumentsComponent'
import StampAndApproveComponent from '../../StampAndApproveComponent'
import { StructuralAnimationStatus } from '../../../reducers/structural'
import { isSelectionIgnored } from '../../../visualization/utils'
import { showStructuralSettingsModal } from '../../../actions/structural'
import { rerunStructuralAutomation } from '../../../services/structuralService'
import BuildingViewModes from '../Components/BuildingViewModes'
import StructuralTypes from './EngineeringPage/StructuralTypes'
import StructuralSettingList from './EngineeringPage/StructuralSettingList'
import ElementProperties from './ArchitecturePage/ElementProperties'
import ElementsList from './ArchitecturePage/ElementsList'
import StructuralObjectTools from './EngineeringPage/StructuralObjectTools'
import {
  ResizablePanel,
  ResizablePanelHeader,
} from '../Components/ResizablePanelComponent'
import { BuildingElementStatus } from '../../../models/Construction'
import {
  CalculationsIcon,
  ObjectsIcon,
  OrbitIcon,
  RuleBookIcon,
} from '../../Svgs'
import { setSelectedEditorTab } from '../../../actions/construction'
import BimComponent from '../../BIM/BimComponent'
import TabContent from '../../TabContent'
import { getBimObjectRefId } from '../../../utils/BimUtil'
import { isUUID } from '../../../utils/Common'
import OrbitComponent from '../Orbit/OrbitComponent'

interface LeftToolbarPanelProps {}

const selectIsDisabledForEditorTab = createSelector(
  (state: RootState) => state.construction.buildingElementStatus,
  (buildingElementStatus) => {
    return buildingElementStatus.some(
      (element) => element.status !== BuildingElementStatus.Complete,
    )
  },
)
const selectIsNonEditableBimObject = createSelector(
  (state: RootState) => state.construction.selectedElementsIds,
  (state: RootState) => state.construction.convertData.building,
  (state: RootState) => state.bim.objects,
  (selectedElementsIds, building, bimObjects) => {
    let isNonEditableBimObject = false
    const selectedElementId = selectedElementsIds[0]
    if (selectedElementId) {
      const bimRefMapping = getBimObjectRefId(building, selectedElementId)
      if (
        bimRefMapping &&
        bimRefMapping.length &&
        bimRefMapping[0].bimobject_ref &&
        isUUID(bimRefMapping[0].bimobject_ref)
      ) {
        const bimObject = bimObjects.find(
          (bimObject) => bimObject.id === bimRefMapping[0].bimobject_ref,
        )
        if (!!bimObject) {
          isNonEditableBimObject = bimObject.isNonEditable ?? false
        }
      }
    }

    return isNonEditableBimObject
  },
)

const structuralSettingsElements = [
  {
    name: 'Shear Wall',
    subItems: [
      {
        name: 'Seismic design factor',
        value: '0.0438',
        unit: 'CD',
        type: 'DEFAULT',
      },
      {
        name: 'Minimum wall length',
        value: '1',
        unit: 'FT',
        type: 'DEFAULT',
      },
      {
        name: 'Shear wall capacity',
        value: '1000',
        unit: 'PLF',
        type: 'DEFAULT',
      },
      {
        name: 'Floor dead load',
        value: '20',
        unit: 'PSF',
        type: 'DEFAULT',
      },
      {
        name: 'Wall dead load',
        value: '10',
        unit: 'PSF',
        type: 'DEFAULT',
      },
    ],
  },
]

const ObjectsComponent = () => {
  const isDisabledForEditorTab = useSelector(selectIsDisabledForEditorTab)
  const isNonEditableBimObject = useSelector(selectIsNonEditableBimObject)
  const isArchitecturePage = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionPage ===
      ConstructionPage.Architecture,
  )
  const isEngineeringPage = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionPage ===
      ConstructionPage.Engineering,
  )
  const structuralAnimationStatus = useSelector(
    (state: RootState) => state.structural.structuralAnimationStatus,
  )
  const selectedTool = useSelector(
    (state: RootState) => state.construction.selectedTool,
  )

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        '& > *': {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        },
      }}
    >
      <Box
        sx={{
          overflowY: 'auto',
          flex: '1 1 auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '&-ms-overflow-style:': {
            display: 'none',
          },
          paddingBottom: '56px',
        }}
      >
        {isArchitecturePage && (
          <>
            <BuildingObjectsComponent
              isDisabled={isArchitecturePage && isDisabledForEditorTab}
            />

            <ElementProperties
              isDisabled={
                (isArchitecturePage && isDisabledForEditorTab) ||
                isNonEditableBimObject
              }
            />
          </>
        )}

        {isEngineeringPage && (
          <>
            <StructuralTypes />
            <StructuralObjectTools
              isDisabled={
                structuralAnimationStatus !==
                StructuralAnimationStatus.Completed
              }
            />
          </>
        )}
      </Box>

      {isArchitecturePage && (
        <ResizablePanel
          header={<ResizablePanelHeader title='Layer List' />}
          content={
            <ElementsList
              isDisabled={
                isDisabledForEditorTab || isSelectionIgnored(selectedTool)
              }
            />
          }
        />
      )}

      {isEngineeringPage && (
        <ResizablePanel
          header={<ResizablePanelHeader title='Settings' />}
          content={<StructuralSettingList items={structuralSettingsElements} />}
        />
      )}
    </Box>
  )
}

const editorTabs = [
  {
    type: EditorTab.Orbit,
    icon: <OrbitIcon />,
    content: <OrbitComponent />,
  },
  {
    type: EditorTab.Objects,
    icon: <ObjectsIcon />,
    content: <ObjectsComponent />,
  },
  {
    type: EditorTab.Bim,
    content: <BimComponent />,
  },
]

const editorDocumentsTabs = [
  {
    type: EditorDocumentsTab.Rulebook,
    icon: <RuleBookIcon />,
  },
  {
    type: EditorDocumentsTab.Calculations,
    label: EditorDocumentsTab.Calculations.toString(),
    icon: <CalculationsIcon />,
  },
]

const EditorTabs = () => {
  const dispatch = useDispatch<any>()
  const isArchitecturePage = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionPage ===
      ConstructionPage.Architecture,
  )
  const isEngineeringPage = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionPage ===
      ConstructionPage.Engineering,
  )
  const isDisabledForEditorTab = useSelector(selectIsDisabledForEditorTab)
  const selectedEditorTab = useSelector(
    (state: RootState) => state.construction.selectedEditorTab,
  )

  const [showStructuralDocuments, setShowStructuralDocuments] =
    useState<boolean>(false)
  const [showStampAndApprovePage, setShowStampAndApprovePage] =
    useState<boolean>(false)

  const handlePanelButtonClick = () => {
    if (isEngineeringPage) {
      setShowStructuralDocuments(true)
    }
  }

  const handleApproveClick = () => {
    if (isEngineeringPage) {
      setShowStructuralDocuments(false)
      setShowStampAndApprovePage(true)
    }
  }

  const handleApproveWindowClose = (showPreviousWindow?: boolean) => {
    setShowStampAndApprovePage(false)
    if (showPreviousWindow) {
      setShowStructuralDocuments(true)
    }
  }

  const selectedIndex = editorTabs.findIndex(
    (tab) => tab.type === selectedEditorTab,
  )

  const handleEditorTabClick = (newValue: number) => {
    dispatch(setSelectedEditorTab(editorTabs[newValue]?.type))
  }
  const boxRef = useRef<HTMLDivElement>(null)
  const [boxHeight, setBoxHeight] = useState(0)

  useEffect(() => {
    const calculateHeight = () => {
      if (boxRef.current) {
        setBoxHeight(boxRef.current.clientHeight)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  return (
    <Box
      sx={{
        width: selectedEditorTab === EditorTab.Bim ? '40rem' : '25rem',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          overflow: 'hidden',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'end',
            backgroundColor: '#F2F2F2',
            borderRadius: '10px 10px 0px 0px',
          }}
        >
          {isEngineeringPage && (
            <Box
              sx={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
              }}
            >
              {/* <Button
                    sx={{
                      height: '32px',
                      minWidth: '120px',
                      borderRadius: '4px',
                      backgroundColor: '#1705E5',
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '14px',
                    }}
                    onClick={() => {
                      dispatch(showStructuralSettingsModal(true))
                    }}
                  >
                    Update Settings
                  </Button> */}
              <Button
                sx={{
                  height: '32px',
                  minWidth: '120px',
                  borderRadius: '4px',
                  backgroundColor: '#1705E5',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '14px',
                  p: 0,
                }}
                onClick={handlePanelButtonClick}
              >
                Documents
              </Button>
              <Button
                sx={{
                  height: '32px',
                  minWidth: '120px',
                  borderRadius: '4px',
                  backgroundColor: '#1705E5',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '14px',
                  p: 0,
                }}
                onClick={() => {
                  dispatch(rerunStructuralAutomation())
                }}
              >
                Run Automation
              </Button>
              <IconButton
                onClick={() => {
                  dispatch(showStructuralSettingsModal(true))
                }}
                sx={{
                  marginRight: '4px',
                }}
              >
                <SvgIcon>
                  /{' '}
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l1.41-.513M5.106 17.785l1.15-.964m11.49-9.642l1.149-.964M7.501 19.795l.75-1.3m7.5-12.99l.75-1.3m-6.063 16.658l.26-1.477m2.605-14.772l.26-1.477m0 17.726l-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205L12 12m6.894 5.785l-1.149-.964M6.256 7.178l-1.15-.964m15.352 8.864l-1.41-.513M4.954 9.435l-1.41-.514M12.002 12l-3.75 6.495'
                    />
                  </svg>
                </SvgIcon>
              </IconButton>
            </Box>
          )}

          {/* <IconButton
              onClick={handlePanelButtonClick}
              sx={{
                marginRight: '4px',
              }}
            >
              <ToolbarPanelIcon
                sx={{
                  fontSize: '14px',
                  color: '#BBBCBC',
                }}
              />
            </IconButton> */}
        </Box>
        <Box ref={boxRef} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              display: 'flex',
              padding: '0.35rem 0.7rem',
              background: '#F2F2F2',
              borderBottom: '1px solid #E4E4E4',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {editorTabs.map((tab, index) => (
                <Fragment key={index}>
                  <Tab
                    disableRipple
                    icon={tab.icon || undefined}
                    label={!tab.icon ? tab.type.toString() : undefined}
                    onClick={() => handleEditorTabClick(index)}
                    sx={{
                      minWidth: '2.1rem',
                      minHeight: 0,
                      height: '100%',
                      alignItems: 'start',
                      color: index === selectedIndex ? '#fff' : '#97999B',
                      background: index === selectedIndex ? '#1705E5' : 'white',
                      fontSize: '0.72rem',
                      display: 'flex',
                      textTransform: 'none',
                      p: '0.15rem 0.35rem',
                      borderRadius: '0.3rem',
                      justifyContent: 'center',
                      opacity: 1,
                      '& .MuiSvgIcon-root': {
                        width: tab.icon ? '0.9rem' : 0,
                      },
                    }}
                  />
                  {index < editorTabs.length - 1 && (
                    <Divider
                      orientation='vertical'
                      flexItem
                      sx={{ mx: 1, my: 0.5 }}
                    />
                  )}
                </Fragment>
              ))}
            </Box>
            <Box gap='0.5rem' sx={{ display: 'flex', alignItems: 'center' }}>
              {editorDocumentsTabs.map((tab, index) => (
                <Tab
                  disableRipple
                  key={index}
                  icon={tab.icon}
                  iconPosition='start'
                  label={tab.label}
                  sx={{
                    minWidth: 0,
                    minHeight: 0,
                    height: '100%',
                    alignItems: 'center',
                    color: '#97999B',
                    background: 'white',
                    fontSize: '0.72rem',
                    display: 'flex',
                    textTransform: 'none',
                    p: '0.15rem 0.35rem',
                    borderRadius: '0.3rem',
                    justifyContent: 'center',
                    opacity: 1,
                    '& .MuiTab-iconWrapper': {
                      margin: 0,
                      width: '0.9rem',
                    },
                    '& .MuiSvgIcon-root': {
                      width: tab.icon ? '0.9rem' : 0,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              padding: '0.7rem',
              boxSizing: 'border-box',
              borderBottom: '1px solid #E4E4E4',
            }}
          >
            <BuildingViewModes
              isDisabled={isArchitecturePage && isDisabledForEditorTab}
            />
          </Box>
        </Box>

        <TabContent
          selectedTabIndex={editorTabs.findIndex(
            (tab) => tab.type === selectedEditorTab,
          )}
          contentHeight={`calc(100% - ${boxHeight}px)`}
        >
          {editorTabs.map((tab, index) => (
            <Fragment key={index}>{tab.content}</Fragment>
          ))}
        </TabContent>
      </Box>
      <StructuralDocumentsComponent
        isOpen={showStructuralDocuments}
        handleApprove={handleApproveClick}
        handleClose={() => setShowStructuralDocuments(false)}
      />
      <StampAndApproveComponent
        isOpen={showStampAndApprovePage}
        handleClose={(showPreviousWindow?: boolean) =>
          handleApproveWindowClose(showPreviousWindow)
        }
      />
    </Box>
  )
}

const TakeOffTab = () => {
  const isItemListPresented = useSelector(
    (state: RootState) => state.takeOff.isItemListPresented,
  )

  return (
    <Box
      sx={{
        width: '296px',
        padding: '10px',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {!isItemListPresented && <BuildingViewModes />}
    </Box>
  )
}

const LeftToolbarPanel: React.FC<LeftToolbarPanelProps> = () => {
  const isEditorTab = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionTab === ConstructionTab.Editor,
  )
  const isValidationTab = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionTab === ConstructionTab.Validation,
  )
  const isTakeOffTab = useSelector(
    (state: RootState) =>
      state.construction.selectedConstructionTab === ConstructionTab.TakeOff,
  )

  return (
    <Box
      sx={{
        display: 'flex',
        borderRadius: '10px',
        pointerEvents: 'auto',
        boxSizing: 'border-box',
        margin: `${isTakeOffTab ? 0 : 12}px`,
        height: isTakeOffTab ? 'fit-content' : 'auto',
        background: isTakeOffTab ? 'transparent' : 'white',
        boxShadow:
          isTakeOffTab || isValidationTab
            ? 'none'
            : '0px 3px 5px 0px rgba(47, 47, 47, 0.25)',
      }}
    >
      {isEditorTab && <EditorTabs />}
      {isValidationTab && <ValidationTab />}
      {isTakeOffTab && <TakeOffTab />}
    </Box>
  )
}

export default LeftToolbarPanel
